import { round } from 'lodash';
import { createSelector } from 'reselect';
import { specialStates, UNIT } from 'src/settings/settings';
import { Withholding } from 'src/types';
import { getDateFrequencyString } from 'src/utils/dates/date-utility';
import {
  shouldApplyStateWithholding,
  shouldDisableStateWithholding,
} from 'src/utils/funds/funds-utility';
import { getUserStateWithholdingRules } from '../stateWithholding/selectors';
import { ApplicationState } from '../types';
import { CalculateWithholdingDollars, FundParts } from './types';

export const getExchangeSubState = ({
  exchangeTransaction,
}: ApplicationState) => exchangeTransaction;

// NOT A SELECTOR. CONVENIENCE FUNCTION

export const calculateWithholdingDollars: CalculateWithholdingDollars = ({
  percent,
  applied,
  grossAmountDollars,
}) => (applied ? Math.round((grossAmountDollars * (percent ?? 0)) / 100) : 0);
/**
 * Gets if the market fluctuation disclosure has been accepted
 * Do not use this directly because it does not cover if it doesn't apply
 */
export const getAcceptedMarketFluctuationDisclosure = createSelector(
  [getExchangeSubState],
  ({ acceptedMarketFluctuationDisclosure }) =>
    acceptedMarketFluctuationDisclosure
);

export const getAcceptedRI2020WithholdingDisclosure = createSelector(
  [getExchangeSubState],
  ({ acceptedRI2020WithholdingDisclosure }) =>
    acceptedRI2020WithholdingDisclosure
);

export const getGrossUnit = createSelector(
  [getExchangeSubState],
  ({ grossUnit }) => grossUnit
);

export const getExchangeContributionYear = createSelector(
  [getExchangeSubState],
  ({ exchangeContributionYear }) => exchangeContributionYear
);

export const getExchangeFederalWithholdingApplied = createSelector(
  [getExchangeSubState],
  ({ federalWithholding: { applied } }) => applied
);

export const getGrossAmount = createSelector(
  [getExchangeSubState],
  ({ grossAmount }) => grossAmount
);

export const getExchangeFederalWithholdingPercent = createSelector(
  [getExchangeSubState],
  ({ federalWithholding: { percent } }) => percent
);

export const getExchangeFromLookup = createSelector(
  [getExchangeSubState],
  ({ exchangeFromLookup }) => exchangeFromLookup
);

export const getExchangeToExistingAllowedAgreementIdLookup = createSelector(
  [getExchangeSubState],
  ({ exchangeToExistingAllowedAgreementIdLookup }) =>
    exchangeToExistingAllowedAgreementIdLookup
);

export const getExchangeToExistingLoadFailed = createSelector(
  [getExchangeSubState],
  ({ exchangeToExistingLoadFailed }) => exchangeToExistingLoadFailed
);

export const getExchangeToExistingLoading = createSelector(
  [getExchangeSubState],
  ({ exchangeToExistingLoading }) => exchangeToExistingLoading
);

export const getExchangeToLookup = createSelector(
  [getExchangeSubState],
  ({ exchangeToLookup }) => exchangeToLookup
);

export const getExchangeToNewAllowedTickerLookup = createSelector(
  [getExchangeSubState],
  ({ exchangeToNewAllowedTickerLookup }) => exchangeToNewAllowedTickerLookup
);

export const getExchangeToNewFundList = createSelector(
  [getExchangeSubState],
  ({ exchangeToNewFundList }) => exchangeToNewFundList
);

export const getExchangeToNewLoading = createSelector(
  [getExchangeSubState],
  ({ exchangeToNewLoading }) => exchangeToNewLoading
);

export const getExchangeType = createSelector(
  [getExchangeSubState],
  ({ exchangeType }) => exchangeType
);

export const getSelectedDays = createSelector(
  [getExchangeSubState],
  ({ selectedDays }) => selectedDays
);

export const getSelectedMonths = createSelector(
  [getExchangeSubState],
  ({ selectedMonths }) => selectedMonths
);

export const getFrequencyString = createSelector(
  [getSelectedDays, getSelectedMonths],
  (selectedDays, selectedMonths) =>
    selectedDays?.length && selectedMonths?.length
      ? getDateFrequencyString({ selectedDays, selectedMonths })
      : ''
);

export const getFederalWithholding = createSelector(
  [getExchangeSubState],
  ({ federalWithholding }) => federalWithholding
);

export const getExchangeFrequency = createSelector(
  [getSelectedDays, getSelectedMonths],
  (selectedDays, selectedMonths) => [selectedDays, selectedMonths]
);

export const getFederalWithholdingDollars = createSelector(
  // capture the current props for grossAmount dollars
  [
    (_, grossAmountDollars: number) => grossAmountDollars,
    getFederalWithholding,
  ],
  (grossAmountDollars, { percent, applied }) =>
    calculateWithholdingDollars({ grossAmountDollars, percent, applied })
);

/**
 * STATE WITHHOLDING IS CALCULATED; NOT IN STATE
 * Use these fund-utility functions to calculate the state withholding
 */
export const getStateWithholding = createSelector(
  [
    (_, props: FundParts) => props,
    getExchangeFederalWithholdingApplied,
    getUserStateWithholdingRules,
    getFederalWithholding,
  ],
  (
    { closeout, grossAmountDollars, grossUnit },
    federalWithholdingApplied,
    userStateWithholdingRules,
    federalWithholding
  ): Withholding => {
    // these are dependent (they come in from the caller)
    const incomingProps = {
      closeout,
      federalWithholdingApplied,
      grossAmountDollars,
      grossUnit,
    };
    const federalWithholdingDollars = calculateWithholdingDollars({
      percent: federalWithholding.percent,
      grossAmountDollars,
      applied: federalWithholding.applied,
    });
    const stateWithholdingApplied: boolean = shouldApplyStateWithholding(
      userStateWithholdingRules,
      incomingProps
    );
    const stateWithholdingDisabled: boolean = shouldDisableStateWithholding(
      userStateWithholdingRules,
      incomingProps
    );

    const statePercentage = userStateWithholdingRules?.percent ?? 0;

    // IIFE, since it's easier to read as simple if/return statements
    // this is a one-for-one copy of the function in funds-utility
    const stateWithholdingDollars: number = (() => {
      if (specialStates.includes(userStateWithholdingRules.stateCode)) {
        return (
          calculateWithholdingDollars({
            percent: statePercentage,
            grossAmountDollars: federalWithholdingDollars,
            applied: stateWithholdingApplied,
          }) ?? 0
        );
      }
      if (!stateWithholdingApplied) {
        return 0;
      }
      if (!stateWithholdingDisabled) {
        return userStateWithholdingRules.rounded
          ? calculateWithholdingDollars({
              percent: statePercentage,
              grossAmountDollars,
              applied: stateWithholdingApplied,
            })
          : round((statePercentage / 100) * grossAmountDollars, 2);
      }
      return 0;
    })();

    // return a Withholding object with the items we're looking for
    return {
      applied: stateWithholdingApplied,
      disabled: stateWithholdingDisabled,
      dollars: stateWithholdingDollars,
      isEstimate: stateWithholdingApplied && grossUnit === UNIT.DOLLARS,
      percent: statePercentage,
    };
  }
);
